import { forwardRef, type HTMLAttributes } from 'react';

import { cn } from '../lib/cn.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * BADGE — A STATUS PILL. Follows the five rules in `Button.tsx`.
 * ═══════════════════════════════════════════════════════════════════════════
 * "Registered", "Failed", "Trial". A word with a tint behind it.
 *
 * ── THE ONE RULE THAT MAKES OR BREAKS IT: WCAG 1.4.1, USE OF COLOUR ────────
 * A badge is the component most often built as a coloured dot with no text — a green pill in the
 * "Status" column and nothing else. That conveys the entire meaning through hue, so it says
 * nothing to a screen reader (a `<span>` with a background colour has no accessible name at
 * all), nothing in forced-colors mode, and nothing to roughly one man in twelve.
 *
 * So **the text is the state and the colour is the emphasis**, in that order, and there is
 * deliberately no way round it: `children` is the meaning, `variant` only tints it, and the
 * optional `dot` is `aria-hidden` because it is a second copy of the tint rather than a second
 * carrier of the information. A badge with no children is a badge with no meaning, and the type
 * says so.
 *
 * ── IT IS NOT A LIVE REGION, AND THAT IS DELIBERATE ────────────────────────
 * `role="status"` here is tempting — a trunk going from "Registered" to "Failed" is exactly the
 * kind of change worth announcing. But a table of forty extensions would then contain forty live
 * regions, and any re-render that touches the list produces a pile-up in which every
 * announcement cancels the last. The live region belongs to whatever OWNS the change (the row's
 * container, the page's status area), where there is one of it. See `Skeleton.tsx` for the same
 * argument about `aria-busy`.
 *
 * ── WHY EVERY VARIANT HAS THE SAME DECORATIVE BORDER ──────────────────────
 * `border-border`, not a tinted one. The `-subtle` fills are close in luminance to the surfaces
 * they sit on by design — that is what makes them subtle — so without an edge the pill's shape
 * is only reliably visible for `danger` and `warning`. `--pbx-border` is the decorative token
 * with no contrast requirement (see tokens.css), which is the correct choice here precisely
 * because the border carries no meaning: it is a boundary, not a signal, and 1.4.11 applies to
 * neither the badge nor its edge since a badge is content and not a control.
 *
 * The fill/text pairs are the ones `tokens.contrast.test.ts` already asserts at 4.5:1 in both
 * themes ("accent text on its own tint" and friends). Do not invent a new pairing here without
 * adding it there.
 */

export type BadgeVariant = 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'info';
export type BadgeSize = 'sm' | 'md';

const VARIANTS: Record<BadgeVariant, string> = {
  neutral: 'bg-surface-sunken text-fg-muted',
  accent: 'bg-accent-subtle text-accent',
  success: 'bg-success-subtle text-success',
  warning: 'bg-warning-subtle text-warning',
  danger: 'bg-danger-subtle text-danger',
  info: 'bg-info-subtle text-info',
};

/**
 * `min-h` rather than `h`, for the reason in `Button.SIZES`: a badge whose label wraps at a
 * user's larger text size must grow rather than clip (WCAG 1.4.4). There is no target-size floor
 * to meet here — a badge is not a control and nothing about it is clickable.
 */
const SIZES: Record<BadgeSize, string> = {
  sm: 'min-h-5 gap-1 px-1.5 py-0.5 text-xs',
  md: 'min-h-6 gap-1.5 px-2 py-0.5 text-sm',
};

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  size?: BadgeSize;
  /**
   * A leading dot in the variant's colour. Purely an emphasis mark — it is `aria-hidden`, and it
   * is never a substitute for the text. See the header.
   */
  dot?: boolean;
}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { variant = 'neutral', size = 'sm', dot = false, className, children, ...rest },
  ref,
) {
  return (
    <span
      ref={ref}
      className={cn(
        'inline-flex items-center rounded-full border border-border font-medium whitespace-nowrap',
        'align-middle',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {dot ? (
        /**
         * `bg-current` — the dot is painted in the badge's own text colour, so it can never
         * disagree with the variant and there is no per-variant dot colour to keep in step.
         * `shrink-0` because a flex item with no intrinsic content will otherwise be squashed to
         * nothing by a long label.
         */
        <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-current" />
      ) : null}
      {children}
    </span>
  );
});
